"use client";

import Link from "@/components/LocalizedLink";
import { useTranslation } from "@/lib/i18n";

/**
 * Header "Status" pill.
 *
 * Previously this component polled /api/status/summary and rendered a
 * coloured operational/degraded/outage badge. The 14 September 2026
 * investigation established that /api/status/summary has no telemetry
 * integration — it returns "operational" as a hardcoded default (with
 * an env-var override for maintenance windows), so the coloured badge
 * was presenting a static default as live network health.
 *
 * Until real telemetry lands, this component renders a neutral
 * "Status" link that goes to /status (a demonstration page). No colour,
 * no live-refresh animation, no health claim. Existing i18n keys are
 * reused so the label reads "Status" / "Статус" per locale.
 *
 * Do not restore the polling colour behaviour without either:
 *   1. wiring real telemetry into /api/status/summary, AND
 *   2. rewriting /status/ to present real operational data instead of
 *      the current demo.
 *
 * See sc-status-honesty-report-2026-09-15.md for the full context.
 */
export function StatusBadge({ compact = false }: { compact?: boolean }) {
  const { t } = useTranslation();
  const label = compact ? t("status_short_unknown") : t("status_unknown");
  return (
    <Link
      href="/status"
      aria-label={`${t("status_aria_prefix")}: ${t("status_unknown")}`}
      className="group inline-flex items-center gap-2 px-2.5 py-1.5 rounded-full bg-surface ring-1 ring-border hover:ring-2 transition-all"
    >
      <span
        aria-hidden="true"
        className="w-2 h-2 rounded-full bg-muted-foreground/60"
      />
      <span className="text-[11px] font-medium text-muted-foreground tracking-wide whitespace-nowrap">
        {label}
      </span>
    </Link>
  );
}
